import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import Layout from '../components/Layout';
import BuyBoxModal from '../components/BuyBoxModal';
import DeleteConfirmationModal from '../components/DeleteConfirmationModal';
import { DEFAULT_CITY, DEFAULT_STATE } from '../config/locations';
import { formatCurrency } from '../utils/formatters';
import { STATUS_CONFIG } from '../config/statusConfig';
import { API_URL } from '../config/api';
import EditNotificationsModal from '../components/EditNotificationsModal';
import { Folder, Mail, BellOff, Calendar, Users, Pencil, Calculator, Scale, Trash2 } from 'lucide-react';
import './SavedPage.css';

// --- Helper: Build human-readable filter summary from criteria ---
const buildFilterSummary = (criteria) => {
    const parts = [];

    if (criteria.min_price || criteria.max_price) {
        const min = criteria.min_price ? formatCurrency(Number(criteria.min_price)) : '';
        const max = criteria.max_price ? formatCurrency(Number(criteria.max_price)) : '';
        if (min && max) parts.push(`${min}–${max}`);
        else if (min) parts.push(`${min}+`);
        else if (max) parts.push(`Up to ${max}`);
    }

    if (criteria.min_beds) parts.push(`${criteria.min_beds}+ Beds`);
    if (criteria.min_baths) parts.push(`${criteria.min_baths}+ Baths`);

    if (criteria.min_sqft || criteria.max_sqft) {
        const min = criteria.min_sqft ? `${Number(criteria.min_sqft).toLocaleString()}` : '';
        const max = criteria.max_sqft ? `${Number(criteria.max_sqft).toLocaleString()}` : '';
        if (min && max) parts.push(`${min}–${max} sqft`);
        else if (min) parts.push(`${min}+ sqft`);
        else parts.push(`Up to ${max} sqft`);
    }

    if (criteria.min_roi) parts.push(`${criteria.min_roi}%+ ROI`);
    if (criteria.min_arv) parts.push(`${formatCurrency(Number(criteria.min_arv))}+ ARV`);

    if (criteria.property_types) {
        const types = criteria.property_types.split(',').map(t => {
            const map = { SINGLE_FAMILY: 'Single Family', CONDOS: 'Condo', MULTI_FAMILY: 'Multi-Family', TOWNHOMES: 'Townhouse', MOBILE: 'Mobile', LAND: 'Land' };
            return map[t.trim()] || t.trim();
        });
        parts.push(types.join(', '));
    }

    if (criteria.investment_types) {
        parts.push(criteria.investment_types.split(',').join(', '));
    }

    if (criteria.max_days_on_market) parts.push(`≤${criteria.max_days_on_market} days`);

    return parts.length > 0 ? parts.join(' · ') : 'No filters applied';
};

const TABS = [
    { key: 'listings', label: 'Saved Listings' },
    { key: 'searches', label: 'Saved Searches' },
    { key: 'scenarios', label: 'Scenarios' },
    { key: 'comparisons', label: 'Comparisons' },
];

const SavedPage = () => {
    const { user, session } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();


    const activeTab = searchParams.get('tab') || 'listings';
    const setActiveTab = (tab) => setSearchParams({ tab });

    // --- Saved Listings State ---
    const [savedListings, setSavedListings] = useState([]);
    const [listingsLoading, setListingsLoading] = useState(true);

    // --- Saved Searches State ---
    const [savedBoxes, setSavedBoxes] = useState([]);
    const [boxesLoading, setBoxesLoading] = useState(true);

    // --- Saved Scenarios State ---
    const [savedScenarios, setSavedScenarios] = useState([]);
    const [scenariosLoading, setScenariosLoading] = useState(true);
    const [propertyAddressMap, setPropertyAddressMap] = useState({});

    // --- Saved Comparisons State ---
    const [savedComparisons, setSavedComparisons] = useState([]);
    const [comparisonsLoading, setComparisonsLoading] = useState(true);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [editingBox, setEditingBox] = useState(null);

    useEffect(() => {
        if (!user) return;
        if (activeTab === 'listings') fetchSavedListings();
        else if (activeTab === 'searches') fetchBuyBoxes();
        else if (activeTab === 'scenarios') fetchScenarios();
        else if (activeTab === 'comparisons') fetchComparisons();
    }, [user, activeTab]);

    // --- Saved Listings ---
    const fetchSavedListings = async () => {
        setListingsLoading(true);
        try {
            const res = await fetch(`${API_URL}/saved-properties`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSavedListings(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setListingsLoading(false);
        }
    };

    const handleUnsave = async (propertyId) => {
        try {
            const res = await fetch(`${API_URL}/saved-properties/${propertyId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) {
                setSavedListings(prev => prev.filter(p => p.property_id !== propertyId));
                showToast('Removed from saved', 'success');
            }
        } catch (err) {
            console.error(err);
        }
    };

    // --- Saved Searches / Buy Boxes ---
    const fetchBuyBoxes = async () => {
        setBoxesLoading(true);
        try {
            const res = await fetch(`${API_URL}/buy-boxes`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSavedBoxes(data);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setBoxesLoading(false);
        }
    };

    // --- Saved Scenarios ---
    const fetchScenarios = async () => {
        setScenariosLoading(true);
        try {
            const res = await fetch(`${API_URL}/scenarios`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setSavedScenarios(data);
                // Fetch addresses for unique property IDs
                const uniqueIds = [...new Set(data.map(s => s.property_id))];
                const addrMap = {};
                await Promise.all(uniqueIds.map(async (pid) => {
                    try {
                        const r = await fetch(`${API_URL}/properties/${pid}`);
                        if (r.ok) {
                            const p = await r.json();
                            addrMap[pid] = `${p.address}, ${p.city}`;
                        }
                    } catch { /* ignore */ }
                }));
                setPropertyAddressMap(addrMap);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setScenariosLoading(false);
        }
    };

    // --- Saved Comparisons ---
    const fetchComparisons = async () => {
        setComparisonsLoading(true);
        try {
            const res = await fetch(`${API_URL}/comparisons`, {
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok) setSavedComparisons(await res.json());
        } catch (err) {
            console.error(err);
        } finally {
            setComparisonsLoading(false);
        }
    };

    const handleApply = (criteria) => {
        const params = new URLSearchParams();
        Object.entries(criteria).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
                params.set(key, value);
            }
        });
        // Default to Des Moines if no location filter
        if (!params.get('city') && !params.get('zip_code')) {
            params.set('city', DEFAULT_CITY);
            params.set('state', DEFAULT_STATE);
        }
        navigate(`/search?${params.toString()}`);
    };

    const handleDeleteClick = (box, e) => {
        e.stopPropagation();
        setItemToDelete(box);
        setIsDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
        if (!itemToDelete) return;
        try {
            let url;
            if (itemToDelete._type === 'scenario') {
                url = `${API_URL}/scenarios/${itemToDelete.id}`;
            } else if (itemToDelete._type === 'comparison') {
                url = `${API_URL}/comparisons/${itemToDelete.id}`;
            } else {
                url = `${API_URL}/buy-boxes/${itemToDelete.id}`;
            }
            const res = await fetch(url, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${session?.access_token}` }
            });
            if (res.ok || res.status === 204) {
                if (itemToDelete._type === 'scenario') {
                    setSavedScenarios(prev => prev.filter(s => s.id !== itemToDelete.id));
                } else if (itemToDelete._type === 'comparison') {
                    setSavedComparisons(prev => prev.filter(c => c.id !== itemToDelete.id));
                } else {
                    setSavedBoxes(prev => prev.filter(b => b.id !== itemToDelete.id));
                }
                showToast('Deleted', 'success');
            }
        } catch (err) {
            console.error(err);
        } finally {
            setItemToDelete(null);
            setIsDeleteModalOpen(false);
        }
    };

    const handleUpdateNotifications = async ({ email_frequency, cc_emails }) => {
        if (!editingBox) return;
        try {
            const res = await fetch(`${API_URL}/buy-boxes/${editingBox.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${session?.access_token}`
                },
                body: JSON.stringify({ email_frequency, cc_emails })
            });
            if (res.ok) {
                const updated = await res.json();
                setSavedBoxes(prev => prev.map(b => b.id === updated.id ? updated : b));
                showToast('Notifications updated', 'success');
                setEditingBox(null);
            } else {
                showToast('Failed to update', 'error');
            }
        } catch (err) {
            console.error(err);
            showToast('Error updating notifications', 'error');
        }
    };



    return (
        <Layout scrollable={true}>
            <div className="saved-page">
                <div className="saved-page-container">

                    {/* Header */}
                    <div className="saved-page-header">
                        <div>
                            <button type="button" className="back-link-saved" onClick={() => navigate(-1)}>← Back</button>
                            <h1 className="saved-page-title">My Saved</h1>
                            <p className="saved-page-subtitle">Manage your saved properties and deal criteria.</p>
                        </div>
                        {activeTab === 'searches' && (
                            <button className="btn-primary-saved" onClick={() => setIsModalOpen(true)}>
                                + Create Buy Box
                            </button>
                        )}
                    </div>

                    {/* Tab Bar */}
                    <div className="saved-tab-bar">
                        {TABS.map(tab => (
                            <button
                                key={tab.key}
                                className={`saved-tab ${activeTab === tab.key ? 'active' : ''}`}
                                onClick={() => setActiveTab(tab.key)}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {/* Tab Content */}
                    {activeTab === 'listings' && (
                        <div className="tab-content">
                            {listingsLoading ? (
                                <div className="saved-loading"><div className="spinner"></div></div>
                            ) : savedListings.length === 0 ? (
                                <div className="saved-empty-state">
                                    <div className="empty-icon-saved">❤️</div>
                                    <h2>No Saved Listings Yet</h2>
                                    <p>Click the heart icon on any property to save it here.</p>
                                    <Link to="/search" className="btn-primary-saved">Browse Properties</Link>
                                </div>
                            ) : (
                                <div className="saved-listings-grid">
                                    {savedListings.map(item => (
                                        <div
                                            key={item.property_id}
                                            className="saved-listing-card"
                                            onClick={() => navigate(`/properties/${item.property_id}`)}
                                        >
                                            <div className="sl-image-wrap">
                                                {item.primary_photo_url ? (
                                                    <img src={item.primary_photo_url} alt={item.address} className="sl-image" />
                                                ) : (
                                                    <div className="sl-image-placeholder">No Photo</div>
                                                )}
                                                <span
                                                    className="sl-status-chip"
                                                    style={{ color: (STATUS_CONFIG[item.listing_status] || STATUS_CONFIG.UNKNOWN).color }}
                                                >
                                                    {(STATUS_CONFIG[item.listing_status] || STATUS_CONFIG.UNKNOWN).label}
                                                </span>
                                                <button
                                                    className="sl-unsave-btn"
                                                    onClick={(e) => { e.stopPropagation(); handleUnsave(item.property_id); }}
                                                    aria-label="Remove from saved"
                                                >
                                                    <svg viewBox="0 0 24 24" width="18" height="18" fill="#ef4444" stroke="#ef4444" strokeWidth="2">
                                                        <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                                                    </svg>
                                                </button>
                                            </div>
                                            <div className="sl-info">
                                                <div className="sl-price">{formatCurrency(item.list_price)}</div>
                                                <div className="sl-details">
                                                    {item.beds || '-'} bd · {item.baths || '-'} ba · {item.sqft ? `${Number(item.sqft).toLocaleString()} sqft` : '-'}
                                                </div>
                                                <div className="sl-address">{item.address}</div>
                                                <div className="sl-metrics">
                                                    <span>ARV {formatCurrency(item.arv)}</span>
                                                    <span className="sl-roi">{item.roi_percentage ? `${item.roi_percentage.toFixed(1)}% ROI` : ''}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'searches' && (
                        <div className="tab-content">
                            {boxesLoading ? (
                                <div className="saved-loading"><div className="spinner"></div></div>
                            ) : savedBoxes.length === 0 ? (
                                <div className="saved-empty-state">
                                    <div className="empty-icon-saved" style={{ marginBottom: '16px', color: '#9ca3af' }}>
                                        <Folder size={48} strokeWidth={1.5} />
                                    </div>
                                    <h2>No Saved Searches Yet</h2>
                                    <p>Save a search from the map or create a custom Buy Box.</p>
                                    <button className="btn-primary-saved" onClick={() => setIsModalOpen(true)}>Create Buy Box</button>
                                </div>
                            ) : (
                                <div className="saved-searches-grid">
                                    {savedBoxes.map(box => (
                                        <div key={box.id} className="search-card" onClick={() => handleApply(box.criteria)}>
                                            <div className="sc-header">
                                                <span className="sc-date">
                                                    {new Date(box.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                                <button
                                                    className="sc-delete-btn"
                                                    onClick={(e) => handleDeleteClick(box, e)}
                                                    aria-label="Delete saved search"
                                                >
                                                    ✕
                                                </button>
                                            </div>
                                            <div className="sc-body">
                                                <h3 className="sc-name">{box.name}</h3>
                                                <p className="sc-summary">{buildFilterSummary(box.criteria)}</p>
                                            </div>
                                            <div className="sc-footer">
                                                <span className="sc-frequency" style={{ display: 'flex', alignItems: 'center' }}>
                                                    {box.email_frequency === 'daily' ? (
                                                        <>
                                                            <Mail size={14} style={{ marginRight: '6px' }} />
                                                            Daily Alerts
                                                        </>
                                                    ) : box.email_frequency === 'weekly' ? (
                                                        <>
                                                            <Calendar size={14} style={{ marginRight: '6px' }} />
                                                            Weekly Alerts
                                                        </>
                                                    ) : box.email_frequency === 'monthly' ? (
                                                        <>
                                                            <Calendar size={14} style={{ marginRight: '6px' }} />
                                                            Monthly Alerts
                                                        </>
                                                    ) : (
                                                        <>
                                                            <BellOff size={14} style={{ marginRight: '6px', opacity: 0.7 }} />
                                                            No Alerts
                                                        </>
                                                    )}
                                                    {box.cc_emails?.length > 0 && (
                                                        <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center' }}>
                                                            <Users size={14} style={{ marginRight: '4px' }} />
                                                            +{box.cc_emails.length} CC
                                                        </span>
                                                    )}
                                                </span>
                                                <button
                                                    className="sc-edit-btn"
                                                    onClick={(e) => { e.stopPropagation(); setEditingBox(box); }}
                                                    aria-label="Edit notifications"
                                                >
                                                    <Pencil size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'scenarios' && (
                        <div className="tab-content">
                            {scenariosLoading ? (
                                <div className="saved-loading"><div className="spinner"></div></div>
                            ) : savedScenarios.length === 0 ? (
                                <div className="saved-empty-state">
                                    <div className="empty-icon-saved" style={{ marginBottom: '16px', color: '#9ca3af' }}>
                                        <Calculator size={48} strokeWidth={1.5} />
                                    </div>
                                    <h2>No Saved Scenarios Yet</h2>
                                    <p>Run a scenario analysis on any property and save it.</p>
                                    <Link to="/search" className="btn-primary-saved">Browse Properties</Link>
                                </div>
                            ) : (
                                <div className="saved-scenarios-grid">
                                    {/* Group by property */}
                                    {Object.entries(
                                        savedScenarios.reduce((groups, s) => {
                                            const key = s.property_id;
                                            if (!groups[key]) groups[key] = [];
                                            groups[key].push(s);
                                            return groups;
                                        }, {})
                                    ).map(([propertyId, scenarios]) => (
                                        <div key={propertyId} className="sn-property-group">
                                            <h3 className="sn-property-heading">
                                                {propertyAddressMap[propertyId] || `Property #${propertyId}`}
                                            </h3>
                                            <div className="sn-cards">
                                                {scenarios.map(scenario => (
                                                    <div
                                                        key={scenario.id}
                                                        className="search-card"
                                                        onClick={() => navigate(`/scenarios/${scenario.property_id}?scenario_id=${scenario.id}`)}
                                                    >
                                                        <div className="sc-header">
                                                            <span className="sc-date">
                                                                {new Date(scenario.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                            </span>
                                                            <button
                                                                className="sc-delete-btn"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setItemToDelete({ ...scenario, _type: 'scenario' });
                                                                    setIsDeleteModalOpen(true);
                                                                }}
                                                                aria-label="Delete scenario"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                        <div className="sc-body">
                                                            <h3 className="sc-name">{scenario.name}</h3>
                                                            {scenario.results?.cashOnCashROI !== undefined && (
                                                                <p className="sc-summary">
                                                                    Cash on Cash ROI: <strong style={{ color: scenario.results.cashOnCashROI >= 5 ? '#059669' : scenario.results.cashOnCashROI >= 0 ? '#d97706' : '#dc2626' }}>
                                                                        {scenario.results.cashOnCashROI.toFixed(1)}%
                                                                    </strong>
                                                                    {scenario.results.monthlyCashFlow !== undefined && (
                                                                        <> &middot; {formatCurrency(scenario.results.monthlyCashFlow)}/mo</>
                                                                    )}
                                                                </p>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'comparisons' && (
                        <div className="tab-content">
                            {comparisonsLoading ? (
                                <div className="saved-loading"><div className="spinner"></div></div>
                            ) : savedComparisons.length === 0 ? (
                                <div className="saved-empty-state">
                                    <div className="empty-icon-saved" style={{ marginBottom: '16px', color: '#9ca3af' }}>
                                        <Scale size={48} strokeWidth={1.5} />
                                    </div>
                                    <h2>No Saved Comparisons Yet</h2>
                                    <p>Compare properties side-by-side and save the comparison.</p>
                                    <Link to="/search" className="btn-primary-saved">Browse Properties</Link>
                                </div>
                            ) : (
                                <div className="saved-searches-grid">
                                    {savedComparisons.map(comp => (
                                        <div
                                            key={comp.id}
                                            className="search-card"
                                            onClick={() => navigate(`/compare?comparison_id=${comp.id}`)}
                                        >
                                            <div className="sc-header">
                                                <span className="sc-date">
                                                    {new Date(comp.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                                                </span>
                                                <button
                                                    className="sc-delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setItemToDelete({ ...comp, _type: 'comparison' });
                                                        setIsDeleteModalOpen(true);
                                                    }}
                                                    aria-label="Delete comparison"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                            <div className="sc-body">
                                                <h3 className="sc-name">{comp.name}</h3>
                                                <p className="sc-summary">
                                                    {comp.property_ids?.length || 0} properties &middot; {comp.columns?.length || 0} scenarios
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                </div>
            </div>

            <BuyBoxModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                onSuccess={() => {
                    setIsModalOpen(false);
                    fetchBuyBoxes();
                }}
            />

            <DeleteConfirmationModal
                isOpen={isDeleteModalOpen}
                onClose={() => setIsDeleteModalOpen(false)}
                onConfirm={confirmDelete}
                itemName={itemToDelete?.name}
            />

            <EditNotificationsModal
                isOpen={!!editingBox}
                box={editingBox}
                onClose={() => setEditingBox(null)}
                onSave={handleUpdateNotifications}
            />
        </Layout>
    );
};

export default SavedPage;
